<template>
  <v-container class="pa-6">
    <v-card class="pa-4">
      <!-- Report Type Selection -->
      <v-select
        v-model="selectedReportType"
        :items="reportTypes"
        label="Select Report Type"
        outlined
        dense
        class="mt-4 mb-6"
      />

      <!-- Common selection logic for most reports -->
      <template v-if="needsPropertyOrLandlordSelection">
        <v-radio-group v-model="selectionType" row class="mb-4">
          <v-radio label="Single Property" value="property"></v-radio>
          <v-radio label="All Properties (by Landlord)" value="landlord"></v-radio>
        </v-radio-group>

        <div v-if="selectionType === 'landlord'">
          <LandlordSelector
            :properties="landlordList"
            :selected-landlord="selectedLandlord"
            @property-selected="updateSelectedLandlord"
          />
        </div>

        <div v-else>
          <property-selector
            :properties="propertyList"
            @property-selected="updateSelectedProperty"
            class="mb-4"
          />
          <v-alert
            v-if="selectedReportType === 'RentRollReport'"
            type="error"
            outlined
            dense
            class="mt-4 mb-6 text-body-2"
          >
            <strong>Select the property name, not a unit.</strong><br>
            Units can be filtered inside the report after generation.
          </v-alert>
        </div>
      </template>

      <!-- Report Display -->
      <v-card v-if="shouldShowReport" class="mt-6 pa-4">
        <v-card-text class="pa-0">
          <component
            :is="selectedReportComponent"
            :key="reportKey"
            ref="activeReport"
            :properties="displayProperties"
            :property="selectedProperty"
            :unit="selectedUnit"
            :landlord="selectedLandlord"
          />
        </v-card-text>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
import PropertySelector from './PropertySelection.vue';
import LandlordSelector from './LandlordSelector.vue';

// Existing reports
import RentRollReport from './reports/RentRollReport.vue';
import DetailedLedgerReport from './reports/DetailedLedgerReport.vue';
import RentRollSummary from './reports/RentRollSummary.vue';
import AgeAnalysisReport from './reports/AgeAnalysisReport.vue';
import LeaseExpiryReport from './reports/LeaseExpiryReport.vue';
import PropertyStatusReport from './reports/PropertyStatusReport.vue';
import LeaseStatusReport from './reports/LeaseStatusReport.vue';
import UnitMonthlyReport from './reports/UnitMonthlyReport.vue';

// New reports (you need to create these components)
import RentInvoicedReport from './reports/RentInvoicedReport.vue';
import RentPaidReport from './reports/RentPaidReport.vue';
import BankSummaryReport from './reports/BankSummaryReport.vue';

import { getDocs, collection, getFirestore } from "firebase/firestore";

const db = getFirestore();

export default {
  components: {
    PropertySelector,
    LandlordSelector,
    RentRollReport,
    DetailedLedgerReport,
    RentRollSummary,
    AgeAnalysisReport,
    LeaseExpiryReport,
    PropertyStatusReport,
    LeaseStatusReport,
    UnitMonthlyReport,
    RentInvoicedReport,
    RentPaidReport,
    BankSummaryReport
  },

  data() {
    return {
      propertyList: [],
      landlordList: [],
      selectedLandlord: null,
      selectedProperty: null,
      selectedUnit: null,

      selectedReportType: null,
      selectionType: 'property',  // unified for most reports

      // You can keep individual types if you want different defaults
      rentRollSelectionType: 'property',
      detailedLedgerSelectionType: 'property',
      // ... etc.

      reportTypes: [
        
        { text: 'Detailed Ledger', value: 'DetailedLedgerReport' },
        { text: 'Rent Roll Summary', value: 'RentRollSummary' },
        { text: 'Age Analysis Report', value: 'AgeAnalysisReport' },
        { text: 'Lease Expiry Report', value: 'LeaseExpiryReport' },
        { text: 'Property Status Report', value: 'PropertyStatusReport' },
        { text: 'Lease Status Report', value: 'LeaseStatusReport' },
        { text: 'Unit Monthly Report', value: 'UnitMonthlyReport' },

        // ── New reports added here ───────────────────────────────────────
        { text: 'Rent Invoiced Report', value: 'RentInvoicedReport' },
        { text: 'Rent Paid Report', value: 'RentPaidReport' },
        { text: 'Bank Summary Report', value: 'BankSummaryReport' },
        { text: 'Rent Roll Report (Detailed)', value: 'RentRollReport' },
      ]
    };
  },

  computed: {
    selectedReportComponent() {
      const map = {
        RentRollReport: 'RentRollReport',
        DetailedLedgerReport: 'DetailedLedgerReport',
        RentRollSummary: 'RentRollSummary',
        AgeAnalysisReport: 'AgeAnalysisReport',
        LeaseExpiryReport: 'LeaseExpiryReport',
        PropertyStatusReport: 'PropertyStatusReport',
        LeaseStatusReport: 'LeaseStatusReport',
        UnitMonthlyReport: 'UnitMonthlyReport',
        RentInvoicedReport: 'RentInvoicedReport',
        RentPaidReport: 'RentPaidReport',
        BankSummaryReport: 'BankSummaryReport'
      };
      return map[this.selectedReportType] || null;
    },

    needsPropertyOrLandlordSelection() {
      // All reports except perhaps very global ones need selection
      return !!this.selectedReportType;
    },

    displayProperties() {
      if (this.selectionType === 'landlord' && this.selectedLandlord) {
        // Filter properties linked to this landlord
        return this.propertyList.filter(p => p.selectedLandlord === this.selectedLandlord.reference);
      } else if (this.selectedProperty) {
        return [this.selectedProperty];
      }
      return [];
    },

    reportKey() {
      return `${this.selectedReportType}-${this.selectedLandlord?.id || 'no'}-${this.selectedProperty?.id || 'no'}`;
    },

    shouldShowReport() {
      if (!this.selectedReportComponent) return false;
      return this.displayProperties.length > 0;
    }
  },

  async created() {
    await this.fetchProperties();
    await this.fetchLandlords();
  },

  methods: {
    async fetchProperties() {
      try {
        const snap = await getDocs(collection(db, "properties"));
        const properties = await Promise.all(snap.docs.map(async doc => {
          const data = doc.data();
          const id = doc.id;
          const unitsSnap = await getDocs(collection(db, "properties", id, "units"));
          const units = unitsSnap.docs.map(u => ({
            id: u.id,
            unitName: u.data().unitName,
            isOccupied: u.data().isOccupied || false
          }));

          return {
            id,
            displayName: data.displayName || 'Unnamed',
            address: data.address,
            units,
            selectedLandlord: data.selectedLandlord || null
          };
        }));

        properties.sort((a, b) => a.displayName.localeCompare(b.displayName));
        this.propertyList = properties;
      } catch (err) {
        console.error("Properties fetch error:", err);
      }
    },

    async fetchLandlords() {
      try {
        const snap = await getDocs(collection(db, "landlords"));
        this.landlordList = snap.docs.map(doc => ({
          id: doc.id,
          name: doc.data().displayAs || doc.data().name || 'Unnamed',
          reference: doc.data().reference || '',
          ...doc.data()
        }));
      } catch (err) {
        console.error("Landlords fetch error:", err);
      }
    },

    updateSelectedLandlord(landlord) {
      this.selectedLandlord = landlord;
      this.selectedProperty = null;
      this.selectedUnit = null;
    },

    updateSelectedProperty(selected) {
      if (!selected) {
        this.selectedProperty = null;
        this.selectedUnit = null;
        return;
      }
      this.selectedProperty = {
        id: selected.id,
        displayName: selected.displayName,
        address: selected.address,
        units: selected.units,
        selectedLandlord: selected.selectedLandlord
      };
      this.selectedUnit = selected.selectedUnit || null;
      this.selectedLandlord = null;
    }
  }
};
</script>
<style scoped>
/* Unified header style - matches your PDF exactly */
.report-header {
  text-align: center;
  margin-bottom: 28px;
}
.report-header img {
  max-height: 70px;
  margin-bottom: 6px;
}
.report-header .logo-text {
  font-size: 1.4rem;
  font-weight: bold;
  letter-spacing: 1px;
}
.report-header h3 {
  font-size: 1.3rem;
  margin: 8px 0 4px;
}
.report-header .property-line {
  font-size: 1.15rem;
  margin: 4px 0;
}
.report-header .period {
  font-size: 1.1rem;
  color: #444;
}
.report-header hr {
  border: none;
  border-top: 2px solid #555;
  margin: 16px auto;
  width: 90%;
}
</style>